import { useState } from 'react';
import { useAppContext } from '@/components/utilities/AppContext';
import { getSubmitErrorText } from '@/utils/form-labels';
import RichText from '@/components/utilities/RichText';
import Logo from '@/components/ui/logo';
import QuizQuestion from './QuizQuestion';
import QuizEmailForm from './QuizEmailForm';
import QuizResult from './QuizResult';

const QuizBlock = ({
  quizId,
  quizTitle,
  emailFormTitle,
  emailFormSubtitle,
  firstNamePlaceholder,
  emailPlaceholder,
  phonePlaceholder,
  consentText,
  submitButtonLabel,
  resultCongratulationTitle,
  resultCongratulationSubtitle,
  resultScoreLabel,
  quizQuestions,
  quizResultLevels
}) => {
  const questions = quizQuestions;
  const { locale, rootPath } = useAppContext();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '' });
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const totalQuestions = questions?.length || 0;
  const emailStep = totalQuestions;
  const resultStep = totalQuestions + 1;

  const handleSelectAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      // Scoring and the Leadhub write (profile, newsletters subscription, `quiz`
      // tag with the score) both happen in the backend — see /api/quiz-submit.
      const res = await fetch('/api/quiz-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quizId,
          pagePath: rootPath,
          locale: locale || 'cs',
          answers,
          firstName: formData.firstName,
          email: formData.email,
          phone: formData.phone
        })
      });
      if (!res.ok) throw new Error('submit-failed');

      const data = await res.json();
      setResult(data);
      setStep(resultStep);

      // Browser-side identify stays so Leadhub can couple this device's cookie
      // (client_id) with the profile — the backend has no access to it.
      if (window.lhi) {
        window.lhi('identify', {
          email: formData.email,
          ...(formData.phone ? { phone: formData.phone } : {}),
          ...(formData.firstName ? { first_name: formData.firstName } : {}),
          subscribe: ['newsletters']
        });
      }
    } catch (error) {
      console.error('Quiz submit error:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-[90vw] flex-col pt-4 pb-8 xl:w-(--container-width)">
      <div className="mb-14 flex justify-center">
        <Logo className="h-[20px] w-[114px] text-white" />
      </div>
      <h2 className="mb-8 text-center font-sans text-[30px] leading-[32.6px] font-medium">
        <RichText>{quizTitle}</RichText>
      </h2>

      <div className="mx-auto max-w-[500px]">
        {step < totalQuestions && questions[step] && (
          <QuizQuestion
            question={questions[step]}
            questionIndex={step}
            totalQuestions={totalQuestions}
            selectedAnswer={answers[questions[step].id]}
            onSelectAnswer={(value) => handleSelectAnswer(questions[step].id, value)}
            onBack={handleBack}
            onNext={handleNext}
          />
        )}

        {step === emailStep && (
          <QuizEmailForm
            emailFormTitle={emailFormTitle}
            emailFormSubtitle={emailFormSubtitle}
            firstNamePlaceholder={firstNamePlaceholder}
            emailPlaceholder={emailPlaceholder}
            phonePlaceholder={phonePlaceholder}
            consentText={consentText}
            submitButtonLabel={submitButtonLabel}
            formData={formData}
            onFormChange={setFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            errorText={submitError ? getSubmitErrorText(locale) : null}
          />
        )}

        {step === resultStep && result && (
          <QuizResult
            result={result}
            congratulationTitle={resultCongratulationTitle}
            congratulationSubtitle={resultCongratulationSubtitle}
            scoreLabel={resultScoreLabel}
          />
        )}
      </div>
    </div>
  );
};

export default QuizBlock;
